import { useState, useEffect, useMemo } from 'react';

import { Button } from '../../components/ui/button';
import { SearchInput } from '../../components/ui/search-input';
import { Play } from 'lucide-react';

import { Test } from '@/types/evals-types';
import { getTests } from '../../services/evaluation/get-tests';

import { generatePath } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';

import { deleteTest } from '@/services/evaluation/delete-test';
import { useNotification } from '@/components/notifications';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { sortByUpdatedAt } from '@/utils/sort-utils';

import { useEvaluationStore } from '@/stores/evaluation-store';
import { ROUTES } from '@/constants/routes';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { TestsConfigTable } from './components/tests-config-table';

export default function TestsPage() {
  const [tests, setTests] = useState<Test[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const [isLoadingTests, setIsLoadingTests] = useState(true);
  const { setTests: setEvaluationStoreTests, removeTest } =
    useEvaluationStore();

  useEffect(() => {
    // Fetch tests and their results on mount
    const fetchTestsAndResults = async () => {
      setIsLoadingTests(true);
      try {
        const testSummaries = await getTests();
        // Sort tests by updated_at in descending order (most recent first)
        const sortedTests = sortByUpdatedAt<Test>(testSummaries);
        setTests(sortedTests);
        setEvaluationStoreTests(sortedTests);
      } catch (error) {
        console.error('Error fetching tests and results', error);
        setTests([]); // fallback to empty
      } finally {
        setIsLoadingTests(false);
      }
    };

    fetchTestsAndResults();
  }, [setEvaluationStoreTests]);

  const handleDeleteTest = async (testId: string) => {
    if (!testId) return;

    setIsDeleteLoading(true);
    try {
      await deleteTest(testId);

      // Update local state
      setTests((prev) => prev?.filter((test) => test.id !== testId));
      removeTest(testId);

      // If we're deleting the currently selected test, reset selection
      if (selectedTestId === testId) {
        setSelectedTestId(null);
      }

      notification.success('Test deleted successfully');
    } catch (error) {
      console.error('Error deleting test:', error);
      notification.error('Failed to delete test');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Filter tests based on search term
  const filteredTests = useMemo(() => {
    if (!searchTerm.trim()) {
      return tests;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return tests.filter((test) => {
      const matchesName = test.name?.toLowerCase().includes(lowerSearchTerm);
      const matchesType = 'content' === lowerSearchTerm;

      return matchesName || matchesType;
    });
  }, [tests, searchTerm]);

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <div className='flex justify-between items-center'>
          <PageTitle title='Tests' />
          <div className='flex items-center gap-2'>
            <Button variant='outline' asChild>
              <a
                href='https://www.loom.com/share/8006a8230b444f6db655d740447eb043?sid=1575e532-e55f-4443-91d0-ff8ab90ef68c'
                target='_blank'
                rel='noopener noreferrer'
              >
                <Play />
                Help
              </a>
            </Button>
            <Button
              onClick={() => {
                navigate(
                  generatePath(ROUTES.EVALUATION.TESTS_SETTINGS, {
                    testId: 'new',
                  }),
                );
              }}
            >
              New test
            </Button>
          </div>
        </div>
        <PageDescription>
          Run sample tests to see how your evaluation works in practice. Try it
          against your content, check if criteria are applied correctly, and
          make adjustments before using it on real generations.
        </PageDescription>
      </div>
      <div className='mb-6'>
        <div className='max-w-96'>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder='Search tests by name or type...'
            aria-label='Search tests'
          />
        </div>
      </div>
      <TestsConfigTable
        testsConfigs={filteredTests}
        isLoading={isLoadingTests}
        setSelectedTestId={setSelectedTestId}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Delete Test'
        description='Are you sure you want to delete this test? All test results and data will be permanently removed. This action cannot be undone.'
        buttonLabel='Delete Test'
        handleDelete={() => selectedTestId && handleDeleteTest(selectedTestId)}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
}
